import React from "react";
import { ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { useLang } from "@/context/LangContext";

const WHATSAPP_URL = "https://wa.me/527712141634";

export const Hero = () => {
  const { t } = useLang();

  return (
    <section
      id="home"
      className="relative overflow-hidden bg-white"
      data-testid="hero-section"
    >
      {/* Decorative gradient blob */}
      <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-green-100/60 blur-3xl" />
      <div className="absolute top-40 -left-32 h-[360px] w-[360px] rounded-full bg-emerald-50 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          {/* SEO-optimized H1 · visible to search engines, hidden visually. Keeps existing visual title. */}
          <h1 className="sr-only" data-testid="seo-h1">
            Terapia Física Pachuca y Quiropráctico Pachuca · TERAFIQUIRO Wellness Center con Fisio Omar
          </h1>
          <span className="inline-flex items-center gap-2 rounded-full bg-green-50 border border-green-100 px-3 py-1 text-xs font-semibold tracking-widest uppercase text-green-700">
            <Sparkles className="h-3.5 w-3.5" /> {t.hero.eyebrow}
          </span>
          <h2 className="mt-6 font-display font-bold text-slate-900 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            {t.hero.title1}
            <br />
            <span className="text-green-600 italic font-medium">{t.hero.title2}</span>
          </h2>
          <p className="mt-3 text-sm font-semibold uppercase tracking-[0.15em] text-green-700" data-testid="hero-location">
            {t.hero.location}
          </p>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-xl">
            {t.hero.desc}
          </p>
          <p className="mt-5 text-base sm:text-lg italic text-green-800 leading-relaxed font-display max-w-xl border-l-2 border-green-300 pl-4" data-testid="hero-welcome">
            {t.about.welcome}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="#booking-fisio"
              className="group inline-flex items-center gap-2 rounded-full bg-green-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-green-700 transition-colors shadow-sm hover:shadow-md"
              data-testid="hero-cta-book"
            >
              {t.hero.cta1}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-white px-6 py-3.5 text-sm font-semibold text-green-700 hover:bg-green-50 transition-colors"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle className="h-4 w-4" />
              {t.hero.cta2}
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { v: t.hero.stat1, s: t.hero.stat1Sub },
              { v: t.hero.stat2, s: t.hero.stat2Sub },
              { v: t.hero.stat3, s: t.hero.stat3Sub },
            ].map((stat, i) => (
              <div key={i} className="border-l-2 border-green-200 pl-4">
                <div className="font-display text-2xl font-bold text-slate-900">{stat.v}</div>
                <div className="text-xs text-slate-500 mt-1 leading-snug">{stat.s}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl shadow-green-900/10 border border-green-50">
            <img
              src="https://images.pexels.com/photos/4506217/pexels-photo-4506217.jpeg"
              alt="Profesional realizando masaje terapéutico"
              className="absolute inset-0 h-full w-full object-cover"
              data-testid="hero-main-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-green-900/30 via-transparent to-transparent" />
          </div>

          <div className="absolute -bottom-6 -left-6 hidden sm:block bg-white rounded-2xl shadow-lg shadow-green-900/5 border border-slate-100 p-5 max-w-[220px] animate-float">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-green-700" />
              </div>
              <div>
                <p className="font-display font-semibold text-sm text-slate-900">+98% Recomienda</p>
                <p className="text-xs text-slate-500">Atención humana</p>
              </div>
            </div>
          </div>

          <div className="absolute -top-4 -right-4 hidden md:block bg-green-600 text-white rounded-2xl shadow-lg p-4 rotate-3">
            <p className="font-display text-xs uppercase tracking-widest opacity-80">Slogan</p>
            <p className="font-display font-semibold text-sm">Al cuidado de tu cuerpo</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
